import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from './AdminNav';
import './Manager.css';
import './AdminPanel.css';
import { API_BASE } from '../config/api';

const ShowcaseManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('new-arrivals');

  const sections = [
    { id: 'new-arrivals', name: 'New Arrivals' },
    { id: 'trending-now', name: 'Trending Now' },
    { id: 'mega-genie-deals', name: 'Mega Genie Deals' },
    { id: 'bestsellers', name: 'Bestsellers' },
    { id: 'clearance-sale', name: 'Clearance Sale' }
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProducts(data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      alert('Error loading products');
    }
  };

  const toArray = (val) => Array.isArray(val) ? val : (val ? [val] : []);

  const handleToggleProduct = async (productId, currentSections) => {
    setLoading(true);
    try {
      const sections = toArray(currentSections);
      const isInSection = sections.includes(activeSection);
      const newSections = isInSection
        ? sections.filter(s => s !== activeSection)
        : [...sections, activeSection];

      // Also remove from featured if being removed from this section
      const product = products.find(p => p._id === productId);
      const currentFeatured = toArray(product.isFeaturedInShowcase);
      const newFeatured = newSections.includes(activeSection)
        ? currentFeatured
        : currentFeatured.filter(s => s !== activeSection);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products/${productId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ showcaseSection: newSections, isFeaturedInShowcase: newFeatured })
      });

      if (response.ok) {
        setProducts(prevProducts =>
          prevProducts.map(p =>
            p._id === productId ? { ...p, showcaseSection: newSections, isFeaturedInShowcase: newFeatured } : p
          )
        );
      } else {
        alert('Error updating product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error updating product');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (productId, currentFeaturedSections) => {
    setLoading(true);
    try {
      const product = products.find(p => p._id === productId);
      const productSections = toArray(product.showcaseSection);
      const featuredSections = toArray(currentFeaturedSections);

      if (!productSections.includes(activeSection)) {
        alert('Product must be added to this section first');
        setLoading(false);
        return;
      }

      const isCurrentlyFeatured = featuredSections.includes(activeSection);

      const featuredCountInSection = products.filter(p =>
        toArray(p.isFeaturedInShowcase).includes(activeSection)
      ).length;

      if (!isCurrentlyFeatured && featuredCountInSection >= 4) {
        alert('You can only feature up to 4 products per section');
        setLoading(false);
        return;
      }

      const newFeaturedSections = isCurrentlyFeatured
        ? featuredSections.filter(s => s !== activeSection)
        : [...featuredSections, activeSection];

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/products/${productId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ isFeaturedInShowcase: newFeaturedSections })
      });

      if (response.ok) {
        setProducts(prevProducts =>
          prevProducts.map(p =>
            p._id === productId ? { ...p, isFeaturedInShowcase: newFeaturedSections } : p
          )
        );
      } else {
        console.error('Error updating featured status');
      }
    } catch (error) {
      console.error('Error updating featured status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProductsInSection = (sectionId) => {
    return products.filter(p => toArray(p.showcaseSection).includes(sectionId));
  };

  const getAvailableProducts = () => {
    return products.filter(p => p.isActive);
  };

  return (
    <div className="admin-panel">
      <AdminNav />

      <div className="manager-container">
        <div className="manager-header">
          <h2>Showcase Sections Manager</h2>
          <div className="showcase-stats">
            {sections.map(section => {
              const sectionProducts = getProductsInSection(section.id);
              const featuredCount = sectionProducts.filter(p => toArray(p.isFeaturedInShowcase).includes(section.id)).length;
              return (
                <div key={section.id} className="stat-item">
                  <span className="stat-label">{section.name}:</span>
                  <span className="stat-value">{sectionProducts.length} total ({featuredCount}/4 featured)</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="showcase-tabs">
          {sections.map(section => (
            <button
              key={section.id}
              className={`showcase-tab ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              {section.name}
              <span className="tab-count">{getProductsInSection(section.id).length}</span>
            </button>
          ))}
        </div>

        <div className="showcase-content">
          <div className="section-info">
            <h3>{sections.find(s => s.id === activeSection)?.name}</h3>
            <p>
              <strong>Click product:</strong> Add/remove from this section<br/>
              <strong>Click star icon:</strong> Mark as featured (max 4) - only featured products appear on homepage
            </p>
          </div>

          <div className="products-grid-showcase">
            {getAvailableProducts().map(product => {
              const productSections = toArray(product.showcaseSection);
              const isInCurrentSection = productSections.includes(activeSection);
              const otherSections = productSections.filter(s => s !== activeSection);
              const isFeatured = isInCurrentSection && toArray(product.isFeaturedInShowcase).includes(activeSection);

              return (
                <div
                  key={product._id}
                  className={`product-showcase-card ${isInCurrentSection ? 'selected' : ''} ${isFeatured ? 'featured' : ''}`}
                >
                  {isInCurrentSection && (
                    <div className="selected-badge">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  )}
                  
                  {isInCurrentSection && (
                    <div 
                      className={`star-toggle ${isFeatured ? 'featured' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFeatured(product._id, toArray(product.isFeaturedInShowcase));
                      }}
                      title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill={isFeatured ? '#fbbf24' : 'none'} stroke="#fbbf24" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                    </div>
                  )}
                  
                  <div 
                    className="product-card-content"
                    onClick={() => handleToggleProduct(product._id, toArray(product.showcaseSection))}
                  >
                  <div className="product-image-showcase">
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={product.images[0].startsWith('http') ? product.images[0] : `${API_BASE.replace('/api', '')}${product.images[0]}`}
                        alt={product.name}
                      />
                    ) : (
                      <div className="placeholder-image">No Image</div>
                    )}
                  </div>
                  
                  <div className="product-details-showcase">
                    <h4>{product.name}</h4>
                    <p className="product-category">{product.categoryLabel || product.category}</p>
                    <p className="product-price-showcase">৳{product.price}</p>
                    {otherSections.length > 0 && (
                      <span className="other-section-badge">
                        Also in: {otherSections.map(s => sections.find(sec => sec.id === s)?.name).filter(Boolean).join(', ')}
                      </span>
                    )}
                    {isFeatured && (
                      <span className="featured-label">Featured</span>
                    )}
                  </div>
                  </div>
                </div>
              );
            })}
          </div>

          {getAvailableProducts().length === 0 && (
            <p className="no-items">No products available. Create some products first!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowcaseManager;
